//
// Software Name: Orange Design System Charts
// SPDX-FileCopyrightText: Copyright (c) 2023 - 2026 Orange SA
// SPDX-License-Identifier: MIT
//
// This software is distributed under the MIT license.
//
export var ODSChartsCSSThemesNames;
(function (ODSChartsCSSThemesNames) {
    ODSChartsCSSThemesNames["NONE"] = "NONE";
    ODSChartsCSSThemesNames["BOOSTED4"] = "BOOSTED4";
    ODSChartsCSSThemesNames["BOOSTED5"] = "BOOSTED5";
    ODSChartsCSSThemesNames["CUSTOM"] = "CUSTOM";
})(ODSChartsCSSThemesNames || (ODSChartsCSSThemesNames = {}));
/**
 * ODSChartsItemCSSDefinition defines the classes or styles to be added on generated HTML element
 */
export class ODSChartsItemCSSDefinition {
    static getClasses(def) {
        return def && def.classes ? def.classes.join(' ') : '';
    }
    static getStyles(def) {
        return def && def.styles
            ? Object.keys(def.styles)
                .map((property) => `${property}: ${def.styles[property]}`)
                .join('; ')
            : '';
    }
}
/**
 * ODSChartsItemGroupCSSDefinition defines the classes an styles to be add on code generated by the library.
 */
export class ODSChartsItemGroupCSSDefinition {
}
export class ODSChartsCSSLegendsDefinition extends ODSChartsItemGroupCSSDefinition {
}
export class ODSChartsCSSPopoverDefinition extends ODSChartsItemGroupCSSDefinition {
}
/**
 * `ODSChartsCSSThemeDefinition` defines the classes and styles to be added on code generated by the library.
 *
 * In general, `ODSChartsCSSThemeDefinition` is not used directly, but one of the instances provided by {@link ODSChartsCSSThemes} is used.
 */
export class ODSChartsCSSThemeDefinition {
    constructor() {
        /**
         * classes an styles to be add to legends generated code.
         */
        this.legends = undefined;
        /**
         * classes an styles to be add to popover and tooltip generated code.
         */
        this.popover = undefined;
    }
}
const BOOSTED5_Definition = {
    legends: {
        odsChartsLegendHolder: {
            classes: ['pt-2', 'pb-3'],
        },
        odsChartsLegendContainer: {
            classes: ['d-flex', 'flex-wrap', 'justify-content-start'],
        },
        odsChartsLegendContainerVertical: {
            classes: ['flex-column'],
        },
        odsChartsLegendContainerHorizontal: {
            classes: ['mx-4'],
        },
        odsChartsLegendItem: { classes: ['mx-2', 'pb-2'] },
        odsChartsLegendCustomContent: { classes: ['ms-1'] },
        odsChartsLegendGlobalCustomContent: { classes: ['ms-2'] },
        odsChartsLegendLink: { classes: ['text-decoration-none'] },
        odsChartsLegendLinkOpacity: { classes: ['opacity-25'] },
        odsChartsLegendColorHolder: {
            classes: ['d-inline-block'],
        },
        odsChartsLegendColor: {
            classes: ['d-block', 'position-relative', 'me-1'],
            styles: {
                width: '12px',
                height: '12px',
            },
        },
        odsChartsLegendLabel: {
            classes: ['fw-normal', 'small'],
        },
    },
    popover: {
        odsChartsPopoverLine: { classes: ['text-nowrap', 'd-flex', 'align-items-baseline', 'mb-1'] },
        odsChartsPopoverColorHolder: {
            classes: ['d-inline-block', 'me-1'],
        },
        odsChartsPopoverColor: {
            classes: ['d-block', 'position-relative'],
            styles: {
                width: '12px',
                height: '12px',
            },
        },
        odsChartsPopoverText: { classes: ['flex-grow-1', 'd-flex', 'align-items-baseline', 'small'] },
        odsChartsPopoverLabel: { classes: ['me-2', 'flex-grow-1'] },
        odsChartsPopoverValue: { classes: ['fw-bold'] },
    },
};
const BOOSTED4_Definition = {
    legends: {
        odsChartsLegendHolder: {
            classes: ['pt-2', 'pb-3'],
        },
        odsChartsLegendContainer: {
            classes: ['d-flex', 'flex-wrap', 'justify-content-start'],
        },
        odsChartsLegendContainerVertical: {
            classes: ['flex-column'],
        },
        odsChartsLegendContainerHorizontal: {
            classes: ['mx-4'],
        },
        odsChartsLegendItem: { classes: ['mx-2', 'pb-2'] },
        odsChartsLegendCustomContent: { classes: ['ml-1'] },
        odsChartsLegendGlobalCustomContent: { classes: ['ml-2'] },
        odsChartsLegendLink: {
            classes: ['text-decoration-none'],
            styles: {
                opacity: '1',
            },
        },
        odsChartsLegendLinkOpacity: {
            classes: ['opacity-25'],
            styles: {
                opacity: '.25',
            },
        },
        odsChartsLegendColorHolder: {
            classes: ['d-inline-block'],
        },
        odsChartsLegendColor: {
            classes: ['d-block', 'position-relative', 'mr-1'],
            styles: {
                width: '12px',
                height: '12px',
            },
        },
        odsChartsLegendLabel: {
            classes: ['font-weight-normal', 'small'],
        },
    },
    popover: {
        odsChartsPopoverLine: { classes: ['text-nowrap', 'd-flex', 'align-items-baseline', 'mb-1'] },
        odsChartsPopoverColorHolder: {
            classes: ['d-inline-block', 'mr-1'],
        },
        odsChartsPopoverColor: {
            classes: ['d-block', 'position-relative'],
            styles: {
                width: '12px',
                height: '12px',
            },
        },
        odsChartsPopoverText: { classes: ['flex-grow-1', 'd-flex', 'align-items-baseline', 'small', 'm-0'] },
        odsChartsPopoverLabel: { classes: ['mr-2', 'flex-grow-1'] },
        odsChartsPopoverValue: { classes: ['font-weight-bold'] },
    },
};
const NONE = {
    legends: { odsChartsLegendHolder: { classes: ['ods-charts-no-css-lib'] } },
    popover: {
        odsChartsPopoverHolder: { classes: ['ods-charts-no-css-lib'] },
        odsChartsPopover: { classes: ['ods-charts-no-css-lib'] },
        odsChartsPopoverBodyContent: { classes: ['ods-charts-no-css-lib'] },
        odsChartsPopoverLine: { classes: ['ods-charts-no-css-lib'] },
    },
};
/**
 * Pre-configured {@link ODSChartsCSSThemeDefinition}
 * - ODSCharts.ODSChartsCSSThemes.NONE: the generated code will use an embedded independent style auto-generated by the library.
 * - ODSCharts.ODSChartsCSSThemes.BOOSTED5: the generated code will use Boosted 5 framework CSS.
 * - ODSCharts.ODSChartsCSSThemes.BOOSTED4: the generated code will use Boosted 4 framework CSS.
 */
export const ODSChartsCSSThemes = {
    BOOSTED4: BOOSTED4_Definition,
    BOOSTED5: BOOSTED5_Definition,
    NONE: NONE,
    CUSTOM: { legends: {}, popover: {} },
};
